// frontend-react/src/pages/league-dashboard/KeeperSelection.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Crown, AlertCircle, Activity, Zap, Calendar } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { useKeeperSelection } from '../../hooks/useKeeperSelection';
import { useKeeperProjections } from '../../hooks/useKeeperProjections';
import { deriveProjectionProvenance, projectionLabel, projectionCaption } from '../../services/projectionProvenance';
import { useMarketValues } from '../../hooks/useMarketValues';
import { useCommissioner } from '../../contexts/CommissionerContext';
import { leaguesAPI } from '../../services/apiService';
import EligiblePlayersList from '../../components/keepers/EligiblePlayersList';
import LoadingSpinner from '../../components/LoadingSpinner';
import { DynastyTable } from '../../services/tableService';
import { computeTotals } from '../../services/tables/statTotals';
import {
createKeeperRosterColumns
} from '../../services/tables/keeperRosterColumns';
import { usePlayerModal } from '../../contexts/PlayerModalContext';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import DraftPicksMatrix from './DraftPicksMatrix';
import DraftPicksTab from '../../components/trades/DraftPicksTab';
import { isPitcherPosition } from '../../utils/playerClassification';

/**
 * Keeper Selection Page
 * Main page for selecting and managing keepers for current and future seasons
 */
const KeeperSelection = () => {
  const { leagueId } = useParams();
  const { isCommissionerMode, activeTeamId } = useCommissioner();
  const { openPlayerModal } = usePlayerModal();
  
  // State for user's team
  const [userTeam, setUserTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState(null);
  
  // State for league data
  const [league, setLeague] = useState(null);
  const [leagueLoading, setLeagueLoading] = useState(true);

  // State for cash/budget data
  const [cashData, setCashData] = useState(null);
  
  // Tab state for batters/pitchers
  const [activeTab, setActiveTab] = useState('hitting');
  
  // Season toggle state (2025 vs 2026)
  const [selectedSeason, setSelectedSeason] = useState('current'); // 'current' or 'projected'

  // Draft picks view toggle ('matrix' | 'team')
  const [draftPicksView, setDraftPicksView] = useState('matrix');

  // Draft picks matrix data
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);
  
  // Determine which team ID to use
  const teamId = isCommissionerMode ? activeTeamId : userTeam?.team_id;
  
  // Fetch league data on mount
  useEffect(() => {
    const fetchLeague = async () => {
      try {
        setLeagueLoading(true);
        console.log('🏟️ Fetching league details for:', leagueId);
        const response = await leaguesAPI.getLeagueDetails(leagueId);
        console.log('🏟️ League API response:', response);
        if (response.success && response.league) {
          console.log('✅ League loaded:', response.league);
          setLeague(response.league);
        } else {
          console.error('❌ League API returned no league:', response);
        }
      } catch (err) {
        console.error('❌ Error fetching league:', err);
      } finally {
        setLeagueLoading(false);
      }
    };
    
    if (leagueId) {
      fetchLeague();
    }
  }, [leagueId]);
  
  // Fetch user's team on mount
  useEffect(() => {
    const fetchUserTeam = async () => {
      if (isCommissionerMode) {
        // In commissioner mode, we use activeTeamId from context
        setTeamLoading(false);
        return;
      }
      
      try {
        setTeamLoading(true);
        setTeamError(null);
        
        const response = await leaguesAPI.getUserTeam(leagueId);
        
        if (response.success && response.team_id) {
          setUserTeam(response);
        } else {
          throw new Error('No team found for this league');
        }
      } catch (err) {
        console.error('Error fetching user team:', err);
        setTeamError(err.message || 'Failed to load your team');
      } finally {
        setTeamLoading(false);
      }
    };
    
    if (leagueId) {
      fetchUserTeam();
    }
  }, [leagueId, isCommissionerMode]);

  // Fetch cash balance when teamId is available
  useEffect(() => {
    const fetchCashData = async () => {
      try {
        const response = await leaguesAPI.getTeamCashBalance(leagueId, teamId);
        if (response.success) {
          setCashData(response);
        }
      } catch (err) {
        console.error('Error fetching cash balance:', err);
      }
    };

    if (leagueId && teamId) {
      fetchCashData();
    }
  }, [leagueId, teamId]);

  // currentYear kept for draft picks and other non-keeper uses
  const currentYear = league?.current_season;
  // NOTE: statsYear, projectionsYear, keeperYear computed AFTER useKeeperSelection hook below

  // Fetch draft picks matrix when Draft Picks tab is active
  useEffect(() => {
    if (activeTab !== 'draft-picks' || !leagueId) return;
    const matrixYear = currentYear;
    const fetchMatrix = async () => {
      try {
        setMatrixLoading(true);
        setMatrixError(null);
        const response = await leaguesAPI.getDraftPicksMatrix(leagueId, matrixYear);
        if (response.success) {
          setMatrixData(response);
        } else {
          setMatrixError('Failed to load draft picks');
        }
      } catch (err) {
        setMatrixError(err.message || 'Failed to load draft picks');
      } finally {
        setMatrixLoading(false);
      }
    };
    fetchMatrix();
  }, [activeTab, leagueId, currentYear]);

  // Current season keeper selection hook
  const {
    eligibleKeepers,
    selectedKeepers,
    status,
    loading,
    saving,
    submitting,
    error,
    assignKeeperPosition,
    removeKeeper,
    selectExtension,
    saveDraft,
    submitKeepers,
    unsubmitKeepers,
    isDirty,
    canSave,
    canSubmit,
    canUnsubmit,
    selectedPlayerIds,
    keeperAssignments,
    semanticSeasons
  } = useKeeperSelection(leagueId, teamId, league?.league_status);

  // Canonical §16 semantic seasons, computed server-side from leagues.current_season
  // (Rule 6) + league_status. NO frontend season math — no `current_season - 1`
  // arithmetic, no `||` fallback. The removed `|| (current_season - 1)` was a forbidden
  // silent-wrong-default (Rule 2): it made the current-stats view show 2025 instead of
  // the canonical historical_season=current_season=2026 in_season whenever semanticSeasons
  // hadn't loaded. historical_season IS get_stats_season (current_season in_season,
  // current_season-1 in setup). Undefined while loading → view stays in its loading
  // state, never a wrong year.
  // MUST be after useKeeperSelection hook which provides semanticSeasons.
  const statsYear = semanticSeasons?.historical_season;
  const projectionsYear = semanticSeasons?.projected_season;
  const keeperYear = semanticSeasons?.keeper_season;
  const projectedYear = projectionsYear;

  // 2026 Projections hook - only load when selectedSeason is 'projected'
  const projections = useKeeperProjections(leagueId, teamId, selectedSeason === 'projected');

  // PROJECTION PROVENANCE (Bible §28.7) — the projections view labels itself from the RESPONSE,
  // never from `semanticSeasons.projected_season`. Config says what season the league INTENDS to
  // project; the payload says what the numbers ACTUALLY are. Those differ until the first
  // preseason snapshot lands (~Oct 2026), and after it lands they differ again for any player the
  // snapshot does not cover. Deriving from config is what made the view claim "2027 Projections"
  // over 2026 season-to-date numbers.
  const projectionProvenance = React.useMemo(
    () => deriveProjectionProvenance(projections.eligibleKeepers),
    [projections.eligibleKeepers]
  );
  // null until the payload carries provenance -> show the neutral mode label, never a guessed
  // year (Rule 2: no silent-wrong-default).
  const projectionsButtonLabel = projectionLabel(projectionProvenance) || 'Projections';
  const projectionsCaption = projectionCaption(projectionProvenance);

  // Bug 1: scope market-values to THIS roster's player ids (MLB ids — the key the MV map
  // is built/looked-up by). The unscoped all-league fetch returns >1 MB and 500s; passing
  // the ~25 roster ids makes it succeed (Rule 2 — pass the right param, no catch-and-blank).
  const rosterPlayerIds = React.useMemo(
    () => eligibleKeepers.map((k) => k?.ids?.mlb).filter(Boolean),
    [eligibleKeepers]
  );
  const { marketValues, loading: marketValuesLoading } = useMarketValues(leagueId, rosterPlayerIds);

  // Keeper limit = the league CONFIG field `num_keepers`. CANONICAL PATH:
  // `league.league_settings.num_keepers` (Bible §5 — league_settings is a nested
  // sub-object; the GET /leagues/{id} payload keeps it nested via
  // merge_complete_settings, it is NOT flattened to top level). The backend derives
  // `max_keepers` FROM league_settings.num_keepers (keepers.py:260), so `num_keepers`
  // and `status.max_keepers` are NOT interchangeable (config vs derived echo). Read
  // ONLY the canonical source (Rule 2/Rule 6) — no `|| status?.max_keepers` alternate.
  // Was `league?.num_keepers` (top level) — a path nothing populates → undefined →
  // the false "Configuration Error". If num_keepers is genuinely absent, the
  // `if (!maxKeepers)` guard below still surfaces it loudly.
  const maxKeepers = league?.league_settings?.num_keepers;
  // displayYear is computed inside renderCurrentSeasonTab (depends on isProjected)

  // Build leaguePositions from position_requirements (for DynastyTable roster-lineup mode)
  const leaguePositions = React.useMemo(() => {
    const positions = { hitting: [], pitching: [] };
    if (league?.roster_settings?.position_requirements && typeof league.roster_settings.position_requirements === 'object') {
      const posReqs = league.roster_settings.position_requirements;
      Object.entries(posReqs).forEach(([positionKey, config]) => {
        const slots = config?.slots || 0;
        if (positionKey === 'P' && slots > 0) {
          positions.pitching.push(...Array(slots).fill('P'));
        } else if (slots > 0 && positionKey !== 'BN' && positionKey !== 'DL' && positionKey !== 'MIN') {
          positions.hitting.push(...Array(slots).fill(positionKey));
        }
      });
    }
    return positions;
  }, [league?.roster_settings?.position_requirements]);
  
  // Filter out minor-league players (auto-kept). "MIN" is a ROSTER-SLOT value, whose
  // canonical source is league_context.roster.position — read ONLY that (Rule 2). The
  // old `|| keeper.info?.position` was alternate-source: info.position is the MLB
  // position (C/SS/…), NEVER "MIN", so it could never identify a minor-leaguer — it only
  // masked an empty roster.position by reading a different field. An unset roster.position
  // is correctly treated as not-MIN (kept), not papered over with the MLB position.
  const filteredEligibleKeepers = React.useMemo(() => {
    return eligibleKeepers.filter(keeper => keeper.league_context?.roster?.position !== 'MIN');
  }, [eligibleKeepers]);
  
  // Create optimistic keeper list (merge saved + locally selected)
  const optimisticKeepers = React.useMemo(() => {
    // Start with saved/final keepers from API
    const saved = selectedKeepers.filter(k =>
      (k.keeper?.status === 'saved' || k.keeper?.status === 'final')
      && keeperAssignments[k.player_id] !== undefined
    );
    const savedPlayerIds = new Set(saved.map(k => k.player_id));
    
    // Add locally selected players that aren't saved yet
    const locallySelected = selectedPlayerIds
      .filter(id => !savedPlayerIds.has(id))
      .map(id => {
        const keeper = eligibleKeepers.find(k => k.player_id === id);
        if (keeper) {
          return {
            ...keeper,
            id: `temp-${id}`,
            keeper: { status: 'unsaved' },
            league_context: {
              ...keeper.league_context,
              roster: {
                ...keeper.league_context?.roster,
                position: keeperAssignments[id]?.roster_position
              }
            }
          };
        }
        // Rule 2: don't silently drop. null here means a SELECTED player_id has no match
        // in the (loaded) eligible list — a real anomaly (a selection that isn't keeper-
        // eligible), not just a loading race. Surface it; still skip from the optimistic
        // view since it can't be rendered. (Guarded on length>0 so loading isn't noise.)
        if (eligibleKeepers.length > 0) {
          console.warn(`KeeperSelection: selected player_id ${id} not found in eligibleKeepers — selection references a non-eligible player (data anomaly), skipped from optimistic view.`);
        }
        return null;
      })
      .filter(k => k !== null);

    return [...saved, ...locallySelected];
  }, [selectedKeepers, selectedPlayerIds, eligibleKeepers, keeperAssignments]);
  
  // Calculate live keeper cost (reflects unsaved extension selections)
  // Bible Rule 9 + § 4.5 amendment: keeper.league_context.financial.contract_salary
  // is null when no current contract (post-decrement); fall through to keeper_proposal.cost.
  const liveKeeperCost = optimisticKeepers.reduce((sum, keeper) => {
    const assignment = keeperAssignments[keeper.player_id];
    const cost = assignment?.extension_salary
      ?? parseFloat(keeper.league_context?.financial?.contract_salary)
      ?? keeper.keeper_proposal?.cost;
    return sum + (cost || 0);
  }, 0);
  
  // Count optimistic keepers
  const selectedCount = optimisticKeepers.length;

  // Create optimistic projected keepers list (merge saved + locally selected)
  const optimisticProjectedKeepers = React.useMemo(() => {
    // Start with saved keepers from projections
    const saved = projections.selectedKeepers.filter(k => k.keeper?.status === 'saved' || k.keeper?.status === 'final');
    const savedPlayerIds = new Set(saved.map(k => k.player_id));

    // Add locally selected players that aren't saved yet
    const locallySelected = projections.selectedPlayerIds
      .filter(id => !savedPlayerIds.has(id))
      .map(id => {
        const keeper = projections.eligibleKeepers.find(k => k.player_id === id);
        if (keeper) {
          return {
            ...keeper,
            id: `temp-${id}`,
            keeper: { status: 'unsaved' }
          };
        }
        return null;
      })
      .filter(k => k !== null);

    return [...saved, ...locallySelected];
  }, [projections.selectedKeepers, projections.selectedPlayerIds, projections.eligibleKeepers]);

  // Show loading spinner on initial load
  if (teamLoading || leagueLoading || (loading && !status)) {
    return <LoadingSpinner message="Loading keeper selection..." />;
  }
  
  // Show error if team couldn't be loaded
  if (teamError) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className={dynastyTheme.components.container}>
          <div className={`
            ${dynastyTheme.classes.bg.error}/10
            border ${dynastyTheme.classes.border.error}
            rounded-md p-6 mb-6
            flex items-start space-x-3
          `}>
            <AlertCircle className={`w-6 h-6 ${dynastyTheme.classes.text.error} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className={`${dynastyTheme.classes.text.error} font-semibold mb-1`}>
                Unable to Load Team
              </p>
              <p className={`${dynastyTheme.classes.text.error} text-sm`}>
                {teamError}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Don't render keeper selection until we have a teamId
  if (!teamId) {
    return <LoadingSpinner message="Loading team information..." />;
  }
  
  // Check if maxKeepers is available
  if (!maxKeepers) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className={dynastyTheme.components.container}>
          <div className={`
            ${dynastyTheme.classes.bg.error}/10
            border ${dynastyTheme.classes.border.error}
            rounded-md p-6 mb-6
            flex items-start space-x-3
          `}>
            <AlertCircle className={`w-6 h-6 ${dynastyTheme.classes.text.error} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className={`${dynastyTheme.classes.text.error} font-semibold mb-1`}>
                Configuration Error
              </p>
              <p className={`${dynastyTheme.classes.text.error} text-sm`}>
                Unable to determine maximum number of keepers for this league. Please contact your commissioner.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }


    // Render Current Season Tab Content (Batters/Pitchers)
    const renderCurrentSeasonTab = () => {
    // Choose data source based on selected season
    const isProjected = selectedSeason === 'projected';
    const displayYear = isProjected ? projectionsYear : statsYear;

    const displayData = isProjected ? optimisticProjectedKeepers : optimisticKeepers;
    const displayCount = displayData.length;
    // For current season, use live cost (reflects unsaved extensions). For projected, use saved salary.
    // Bible Rule 9 + § 4.5 amendment: contract_salary may be null; fall through to keeper_proposal.cost.
    const displaySalary = isProjected
      ? displayData.reduce((sum, keeper) => sum + parseFloat(
          keeper.league_context?.financial?.contract_salary
          ?? keeper.keeper_proposal?.cost
          ?? 0
        ), 0)
      : liveKeeperCost;

    const needsExtension = isProjected
      ? displayData.filter(k => k.league_context?.financial?.contract_type === '1').length
      : 0;

    const expiringCount = isProjected
      ? displayData.filter(k => k.league_context?.financial?.contract_type === 'OP').length
      : 0;
    
    const eligibleList = isProjected ? projections.eligibleKeepers : filteredEligibleKeepers;
    const selectedIds = isProjected ? projections.selectedPlayerIds : selectedPlayerIds;
    const isLoading = isProjected ? projections.loading : loading;

    // DD-STAT-CONSOLIDATION C3: native TEAM TOTALS via the SSoT engine. Projected mode
    // routes through the SAME engine via a configurable component source (getComponents:
    // components live at player.projected.stats, not player.stats.[window]) — one engine,
    // no parallel totals path. Absent components contribute nothing → honest '-'.
    const keeperPlayers = (displayData || []).filter(p => p && p.ids);
    const keeperTotalsRow = (['hitting', 'pitching'].includes(activeTab) && keeperPlayers.length >= 2) ? {
      isTotalsRow: true,
      __stats: {
        [activeTab]: computeTotals(keeperPlayers, isProjected
          ? { statType: activeTab, getComponents: (p) => p.projected?.stats?.[activeTab] }
          : { statType: activeTab, window: 'prior_season' }),
      },
    } : null;

    return (
      <>
        {/* Compact Status Panel */}
        <div className={`${dynastyTheme.components.card.base} p-4 mb-6`}>
          <div className="flex items-center justify-between">
            {/* Left: Status Info */}
            <div className="flex items-center space-x-6">
              {!isProjected && (
                <div>
                  <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                    status?.is_submitted 
                      ? `${dynastyTheme.classes.bg.success}/20 ${dynastyTheme.classes.text.success}` 
                      : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutralLight}`
                  }`}>
                    {status?.is_submitted ? 'Submitted' : 'Not Started'}
                  </span>
                </div>
              )}
              <div>
                <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Selected:</span>
                <span className={`ml-2 ${dynastyTheme.classes.text.white} font-bold`}>
                  {displayCount}/{maxKeepers}
                </span>
              </div>
              <div>
                <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Keeper Cost:</span>
                <span className={`ml-2 ${dynastyTheme.classes.text.success} font-bold`}>
                  ${Math.round(displaySalary)}
                </span>
              </div>
              {!isProjected && cashData?.dead_money > 0 && (
                <div>
                  <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Dead Money:</span>
                  <span className={`ml-2 ${dynastyTheme.classes.text.error} font-bold`}>
                    ${Math.round(cashData.dead_money)}
                  </span>
                </div>
              )}
              {!isProjected && league?.salary_settings?.draft_cap != null && (
                <div>
                  <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Available:</span>
                  <span className={`ml-2 font-bold ${
                    league.salary_settings.draft_cap - displaySalary - (cashData?.dead_money || 0) < 0
                      ? dynastyTheme.classes.text.error
                      : dynastyTheme.classes.text.white
                  }`}>
                    ${Math.round(league.salary_settings.draft_cap - displaySalary - (cashData?.dead_money || 0))}
                  </span>
                  <span className={`ml-1 ${dynastyTheme.classes.text.neutralDark} text-xs`}>
                    / ${Math.round(league.salary_settings.draft_cap)}
                  </span>
                </div>
              )}
              {isProjected && needsExtension > 0 && (
                <div>
                  <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Need Extension:</span>
                  <span className={`ml-2 ${dynastyTheme.classes.text.warning} font-bold`}>
                    {needsExtension}
                  </span>
                </div>
              )}
              {isProjected && expiringCount > 0 && (
                <div>
                  <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Expiring:</span>
                  <span className={`ml-2 ${dynastyTheme.classes.text.error} font-bold`}>
                    {expiringCount}
                  </span>
                </div>
              )}
              <div>
                <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>Year:</span>
                <span className={`ml-2 ${dynastyTheme.classes.text.primary} font-bold`}>
                  {displayYear}
                </span>
              </div>
            </div>
          
          {/* Right: Action Buttons */}
          <div className="flex items-center space-x-3">
          {isProjected ? (
          <>
            <button
              onClick={projections.saveDraft}
            disabled={!projections.canSave || projections.saving}
            className={`
            px-4 py-2 rounded font-medium text-sm
            ${projections.canSave && !projections.saving
                ? `${dynastyTheme.components.button.secondary}`
                : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutral} cursor-not-allowed`
                }
                  ${dynastyTheme.classes.transition}
              `}
              >
                  {projections.saving ? 'Saving...' : 'Save Projection Draft'}
              </button>
            
          <button
            onClick={projections.clearAllSelections}
            disabled={displayCount === 0}
          className={`
            px-4 py-2 rounded font-medium text-sm
          ${displayCount > 0
            ? `${dynastyTheme.components.button.danger}`
              : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutral} cursor-not-allowed`
            }
              ${dynastyTheme.classes.transition}
              `}
          >
              Clear All
              </button>
          </>
          ) : (
          <>
          <button
          onClick={saveDraft}
          disabled={!canSave || saving}
          className={`
          px-4 py-2 rounded font-medium text-sm
            ${canSave && !saving
              ? `${dynastyTheme.components.button.secondary}`
                : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutral} cursor-not-allowed`
                }
              ${dynastyTheme.classes.transition}
              `}
              >
                  {saving ? 'Saving...' : 'Save Draft'}
                  </button>

                {status?.is_submitted ? (
                      <button
                        onClick={unsubmitKeepers}
                      disabled={!canUnsubmit || submitting}
                    className={`
                    px-4 py-2 rounded font-medium text-sm
                  ${canUnsubmit && !submitting
                      ? `${dynastyTheme.components.button.warning}`
                      : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutral} cursor-not-allowed`
                  }
                    ${dynastyTheme.classes.transition}
                    `}
                  >
                    {submitting ? 'Unlocking...' : 'Unlock'}
                </button>
              ) : (
              <button
                onClick={submitKeepers}
                disabled={!canSubmit || submitting}
                className={`
                  px-4 py-2 rounded font-medium text-sm
                    ${canSubmit && !submitting
                      ? `${dynastyTheme.components.button.primary}`
                      : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutral} cursor-not-allowed`
                    }
                    ${dynastyTheme.classes.transition}
                  `}
                >
                  {submitting ? 'Submitting...' : 'Submit Keepers'}
                </button>
                )}
                </>
                )}
          </div>
            </div>
          </div>

          {/* Selected Keepers Table - TOP */}
          <div className="mb-6">
          <div className={`${dynastyTheme.components.card.base} overflow-hidden`}>
          <div className="p-4 border-b border-neutral-700">
            <h3 className={`${dynastyTheme.classes.text.primary} font-bold text-lg`}>
              Selected Keepers ({displayCount}/{maxKeepers}) for {keeperYear}
            </h3>
          </div>

          <DynastyTable
            data={displayData}
            columns={createKeeperRosterColumns({
            onPlayerClick: (player) => openPlayerModal(player.ids?.mlb, player),
            onRemoveKeeper: isProjected
            ? (playerId) => {
            const keeper = projections.eligibleKeepers.find(k => k.player_id === playerId);
            if (keeper) projections.toggleKeeperSelection(keeper);
            }
            : removeKeeper,
            isLocked: !isProjected && status?.is_submitted,
            playerType: activeTab,
            leagueSettings: league,
            season: isProjected ? 'projected' : 'current',
              marketValues: marketValues,
              projectionsYear: projectionsYear,
              onSelectExtension: isProjected ? null : selectExtension,
              extensionSelections: isProjected ? {} : keeperAssignments
            })}
            tableType="roster-lineup"
            leaguePositions={leaguePositions}
            playerType={activeTab}
            rowsPerItem={1}
            rowTypes={['season']}
            showTotals={!!keeperTotalsRow}
            totalsRow={keeperTotalsRow}
            maxHeight="400px"
            enableHorizontalScroll={true}
            stickyHeader={true}
          />
        </div>
      </div>

      {/* Eligible Players List - BOTTOM */}
      <div>
        <EligiblePlayersList
          eligibleKeepers={eligibleList.filter(keeper => {
            const position = keeper.info?.position || '';
            const isBatter = !isPitcherPosition(position);
            return activeTab === 'hitting' ? isBatter : !isBatter;
          })}
          selectedPlayerIds={selectedIds}
          keeperAssignments={isProjected ? {} : keeperAssignments}
          maxKeepers={maxKeepers}
          league={league}
          loading={isLoading}
          onAssignPosition={(!isProjected && status?.is_submitted) ? null : (isProjected
            ? (playerId, position) => {
                const keeper = projections.eligibleKeepers.find(k => k.player_id === playerId);
                if (keeper) projections.toggleKeeperSelection(keeper);
              }
            : assignKeeperPosition
          )}
          onRemoveKeeper={(!isProjected && status?.is_submitted) ? null : (isProjected
            ? (playerId) => {
                const keeper = projections.eligibleKeepers.find(k => k.player_id === playerId);
                if (keeper) projections.toggleKeeperSelection(keeper);
              }
            : removeKeeper
          )}
        />
      </div>

      {/* Help Section */}
      <div className={`${dynastyTheme.components.card.base} p-6 mt-8`}>
        <h3 className={`${dynastyTheme.components.heading.h3} flex items-center space-x-2`}>
          <span>How Keeper Selection Works</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-2`}>
              1. Select Your Keepers
            </h4>
            <p className={dynastyTheme.classes.text.neutralLight}>
              Choose players from your roster with active contracts. You can select up to your league's maximum keeper limit.
            </p>
          </div>
          <div>
            <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-2`}>
              2. Save Your Draft
            </h4>
            <p className={dynastyTheme.classes.text.neutralLight}>
              Save your selections as a draft. You can change your mind and update your draft selections anytime before submitting.
            </p>
          </div>
          <div>
            <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-2`}>
              3. Submit to Lock In
            </h4>
            <p className={dynastyTheme.classes.text.neutralLight}>
              When you're confident in your choices, submit your keepers to lock them in. You can unlock during the setup phase if needed.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
  const renderDraftPicksTab = () => {
    const matrixYear = projectionsYear;

    return (
      <div className="space-y-4">
        {/* View toggle — top right */}
        <div className="flex items-center justify-end">
          <button
            onClick={() => setDraftPicksView(v => v === 'matrix' ? 'team' : 'matrix')}
            className={`px-4 py-2 rounded font-medium text-sm ${dynastyTheme.classes.transition} ${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutralLight} hover:text-white`}
          >
            {draftPicksView === 'matrix' ? 'Team View' : 'Matrix View'}
          </button>
        </div>

        {draftPicksView === 'matrix' ? (
          <DraftPicksMatrix
            data={matrixData}
            loading={matrixLoading}
            error={matrixError}
            onRetry={() => {
              setMatrixData(null);
              setMatrixError(null);
              setMatrixLoading(true);
              leaguesAPI.getDraftPicksMatrix(leagueId, matrixYear)
                .then(r => {
                  if (r.success) setMatrixData(r);
                  else setMatrixError('Failed to load draft picks');
                })
                .catch(e => setMatrixError(e.message || 'Failed to load draft picks'))
                .finally(() => setMatrixLoading(false));
            }}
          />
        ) : (
          <DraftPicksTab
            leagueId={leagueId}
            userTeam={userTeam}
            league={league}
            selectedPicks={[]}
            onPickToggle={null}
            readOnly={true}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <TeamPageHeader
        title="Off Season"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
      />

        {/* Error Display */}
        {((selectedSeason === 'current' && error) || (selectedSeason === 'projected' && projections.error)) && (
          <div className={`
            ${dynastyTheme.classes.bg.error}/10
            border ${dynastyTheme.classes.border.error}
            rounded-md p-4 mb-6
            flex items-start space-x-3
          `}>
            <AlertCircle className={`w-5 h-5 ${dynastyTheme.classes.text.error} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className={`${dynastyTheme.classes.text.error} font-semibold mb-1`}>
                Error
              </p>
              <p className={`${dynastyTheme.classes.text.error} text-sm`}>
                {selectedSeason === 'current' ? error : projections.error}
              </p>
            </div>
          </div>
        )}

        {/* Season Year Toggle */}
        <div className="mb-1 mt-[4px]">
          <div className="flex items-center gap-2">
            <span className={`${dynastyTheme.classes.text.neutralLight} text-sm font-medium mr-2`}>Stats Year:</span>
            <button
              onClick={() => setSelectedSeason('current')}
              className={`px-4 py-2 rounded-l-md font-medium text-sm ${dynastyTheme.classes.transition} ${
                selectedSeason === 'current'
                  ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`
                  : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutralLight} hover:bg-neutral-600/30`
              }`}
            >
              {statsYear} Stats
            </button>
            <button
              onClick={() => setSelectedSeason('projected')}
              className={`px-4 py-2 rounded-r-md font-medium text-sm ${dynastyTheme.classes.transition} ${
                selectedSeason === 'projected'
                  ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`
                  : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutralLight} hover:bg-neutral-600/30`
              }`}
            >
              {projectionsButtonLabel}
            </button>
          </div>
          {selectedSeason === 'projected' && projectionsCaption && (
            <div
              data-testid="projection-provenance-note"
              className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}
            >
              {projectionsCaption}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className={`flex items-center justify-between border-b ${dynastyTheme.classes.border.neutral} mb-6`}>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('hitting')}
              className={`px-6 py-3 font-medium ${dynastyTheme.classes.transition} ${
                activeTab === 'hitting'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                  : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Keeper Batters
            </button>
            <button
              onClick={() => setActiveTab('pitching')}
              className={`px-6 py-3 font-medium ${dynastyTheme.classes.transition} ${
                activeTab === 'pitching'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                  : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
              }`}
            >
              <Zap className="w-4 h-4 inline mr-2" />
              Keeper Pitchers
            </button>
            <button
              onClick={() => setActiveTab('draft-picks')}
              className={`px-6 py-3 font-medium ${dynastyTheme.classes.transition} ${
                activeTab === 'draft-picks'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                  : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Draft Picks
            </button>
          </div>

          {/* Projection Loading Indicator */}
          {selectedSeason === 'projected' && projections.loading && (
            <div className="flex items-center gap-3 pr-4">
              <div className="w-32 h-2 bg-neutral-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-400 rounded-full animate-pulse"
                  style={{ width: '60%', animation: 'pulse 1.5s ease-in-out infinite, progress 2s ease-in-out infinite' }}
                />
              </div>
              <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
                Loading projections...
              </span>
            </div>
          )}
        </div>

      {/* Tab Content */}
      {activeTab === 'draft-picks' ? renderDraftPicksTab() : renderCurrentSeasonTab()}
    </div>
  );
};

export default KeeperSelection;
